import React, {useState} from 'react';
import {PencilIcon, PlusIcon, TrashIcon} from '@heroicons/react/24/outline';
import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import {FieldsManager} from '../../../../data/services/fields';
import Resources from '../../../../data/services/resources';
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from '../../../../util/util-constants';
import {numberWithCommas, numberWithCommasToBack} from '../../../../util/util-formaters';
import ModalSaveResource from '../../../../common/components/modal/modal-save-resource';
import FieldCellSelectSearch from '../../../../common/components/fields/field-cell-select-search';
import NoRecords from '../../../../common/components/no-records-found/no-records';
import ButtonIcon from '../../../../common/components/button/button-icon';
import Tooltip from '../../../../common/components/tooltip';
import {genericMoneyFormatter} from '../../../../common/util/util-vanilla';
import {classNames} from '../../../../common/util/util-helpers';

const metadata = {
    ProductServiceID: {
        api: 'api/' + Resources.ProductsServices,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({label: item.ProductService, value: item.ProductServiceID, metadata: item}),
    },
    TaxID: {
        api: 'api/' + Resources.AccountingTaxes,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({label: item.Tax, value: item.TaxID, amount: item.Amount}),
    },
    AccountID: {
        api: 'api/' + Resources.AccountsQuick,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({value: item.AccountID, label: item.AccountNumber + ' ' + item.AccountName, metadata: item}),
    },
};

export default function BillExpenseOcrItemsTable({translate, items, getFields, onAddNewItem, onEditItem, onRemoveItem}) {
    const [createModalOpen, setCreateModalOpen] = useState(() => false);
    const [editModalOpen, setEditModalOpen] = useState(() => false);
    const [selectedItem, setSelectedItem] = useState(() => ({}));
    const [createModalKey, setCreateModalKey] = useState(() => 0);
    const [editModalKey, setEditModalKey] = useState(() => 10000);

    /** UI Events
     ================================================================= */
    function handleToggleCreateModal() {
        setCreateModalOpen((prev) => !prev);
    }

    function handleToggleEditModal(item = {}) {
        setSelectedItem(item);
        setEditModalOpen((prev) => !prev);
    }

    function handleDeleteItem(item) {
        onRemoveItem(item.index);
    }

    function handleAccountInlineChange(index, value) {
        const updatedFields = handleInputChange(items[index], 'AccountID', value);
        onEditItem(updatedFields, index);
    }

    // Trimmed mirror of InvoiceExpenseItemTable's handleInputChange: only the ProductServiceID
    // auto-fill and the Qty/Amount/TaxID total recompute apply to this lean item shape (no
    // Assets/PrepaidExpense/multi-target ChargeTo/split logic here).
    function handleInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);

        let updateTotal = false;

        if (name === 'ProductServiceID') {
            fields = FieldsManager.updateField(fields, 'AccountID',
                value
                    ? {label: value.metadata?.ExpenseAccount, value: value.metadata?.ExpenseAccountID}
                    : ''
            );
            fields = FieldsManager.updateField(fields, 'Description', value?.metadata?.ExpenseDescription ?? '');
            fields = FieldsManager.updateField(fields, 'Amount', value?.metadata?.ExpensePrice ?? 0);
            updateTotal = true;
        }

        if (name === 'Qty' || name === 'Amount' || name === 'TaxID' || updateTotal) {
            const total = (
                numberWithCommasToBack(fields.Qty.value)
                * numberWithCommasToBack(fields.Amount.value)
                * numberWithCommasToBack(fields.TaxID.value?.amount ?? 1)
            );

            const totalCalculation = numberWithCommas(Math.round(total * 100) / 100) ? numberWithCommas(Math.round(total * 100) / 100) : 0;

            fields = FieldsManager.updateField(fields, 'TotalAmount', totalCalculation);
        }

        if (name === 'Qty' && (value === 0 || value === '0')) {
            fields = FieldsManager.updateField(fields, 'Qty', 1);
        }

        fields[name].errorMessage = '';

        return fields;
    }

    /** Helpers
     ================================================================= */
    function getFormattedItems() {
        return items.map((it, index) => {
            const descriptionInvalid = !!it.Description?.errorMessage;
            const accountInvalid = !!it.AccountID?.errorMessage;
            const amountInvalid = !!it.Amount?.errorMessage;
            const isInvalid = !FieldsManager.checkFieldsForErrors(it);
            // Anything invalid that isn't one of the three mapped fields above (e.g. Qty) has
            // nowhere dedicated to show — surface it on Description as a fallback so it's still
            // visible somewhere.
            const unmappedInvalid = isInvalid && !descriptionInvalid && !accountInvalid && !amountInvalid;

            return Object.assign({}, FieldsManager.getFieldKeyValues(it), {
                index: index,
                Account: it.AccountID?.value?.label ?? '',
                Tax: it.TaxID?.value?.label ?? '',
                TaxAmount: it.TaxID?.value?.amount,
                isInvalid: isInvalid,
                descriptionInvalid: descriptionInvalid,
                accountInvalid: accountInvalid,
                amountInvalid: amountInvalid,
                unmappedInvalid: unmappedInvalid,
            });
        });
    }

    const data = getFormattedItems();
    const totalAmountTotal = data.reduce((memo, it) => memo + numberWithCommasToBack(it.TotalAmount), 0);

    /** Render
     ================================================================= */
    return (
        <div>
            <div className="flex items-center justify-between px-6">
                <p className="text-lg leading-6 font-medium text-tm-gray-900">{translate('text.items')}</p>

                <button
                    onClick={handleToggleCreateModal}
                    className="btn btn-primary btn-xs rounded-btn"
                >
                    <PlusIcon className="w-4 h-4 mr-1"/>

                    {translate('btn.add_item')}
                </button>
            </div>

            {!data.length && (
                <div className="py-4">
                    <NoRecords
                        canCreate={true}
                        show={true}
                        title={translate('text.no_items')}
                        text={translate('text.disabled_until_item_add')}
                        btnLabel={translate('btn.add_item')}
                        onBtnClick={handleToggleCreateModal}
                    />
                </div>
            )}

            {!!data.length && (
                <React.Fragment>
                    {/* Mobile card list */}
                    <div className="md:hidden space-y-3 px-6 mt-4">
                        {data.map((it) => (
                            <div
                                key={it.index}
                                className={classNames(
                                    "rounded-xl border border-tm-gray-200 bg-tm-gray-50 p-4",
                                    it.isInvalid && "border-l-4 border-red-500"
                                )}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-1 min-w-0">
                                        <p className="font-medium text-tm-gray-900 truncate">
                                            <span className="text-tm-gray-400 font-normal">{it.index + 1}.</span> {it.Description}
                                        </p>

                                        {(it.descriptionInvalid || it.unmappedInvalid || it.amountInvalid) && (
                                            <Tooltip content={translate('text.missing_required_fields')}>
                                                <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0"/>
                                            </Tooltip>
                                        )}
                                    </div>

                                    <div className="flex-shrink-0 flex gap-1">
                                        <ButtonIcon appearance="border" tooltip={translate('btn.edit')} onClick={() => handleToggleEditModal(it)}>
                                            <PencilIcon className="w-4 h-4"/>
                                        </ButtonIcon>

                                        {data.length > 1 && (
                                            <ButtonIcon appearance="border" tooltip={translate('btn.delete')} onClick={() => handleDeleteItem(it)}>
                                                <TrashIcon className="w-4 h-4"/>
                                            </ButtonIcon>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between gap-2 mt-1">
                                    <div className="min-w-0 flex-1">
                                        <FieldCellSelectSearch
                                            name="AccountID"
                                            value={{value: it.AccountID, label: it.Account}}
                                            onChange={(name, value) => handleAccountInlineChange(it.index, value)}
                                            placeholder={translate('text.no_account_selected')}
                                            isClearable={true}
                                            select={metadata.AccountID}
                                            errorMessage={items[it.index]?.AccountID?.errorMessage}
                                            translate={translate}
                                            openMenuOnFocus={true}
                                        />
                                    </div>

                                    {Number(it.Qty) > 1 ? (
                                        <div className="flex flex-col items-end shrink-0">
                                            <span className="text-sm text-tm-gray-900">{genericMoneyFormatter(it.Amount)}</span>

                                            <span className="text-xs text-tm-gray-500">x{it.Qty}</span>

                                            <span className="font-medium text-tm-gray-900">{genericMoneyFormatter(it.TotalAmount)}</span>
                                        </div>
                                    ) : (
                                        <span className="font-medium text-tm-gray-900 shrink-0">{genericMoneyFormatter(it.Amount)}</span>
                                    )}
                                </div>
                            </div>
                        ))}

                        <div className="flex items-center justify-between px-1 pt-2 font-medium text-tm-gray-900 border-t border-tm-gray-200">
                            <span>{translate('text.total')}</span>

                            <span>{genericMoneyFormatter(totalAmountTotal)}</span>
                        </div>
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block mt-4">
                        {/* table-fixed + explicit per-column widths keep every column's width
                            locked to the header row, so opening the Account select (or any long
                            content) can no longer widen a column — matching the "pinned widths"
                            approach already used in resource-table/index.jsx. */}
                        <table className="min-w-full table-fixed divide-y divide-tm-gray-200">
                            <thead>
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-tm-gray-500 w-[6%]"></th>

                                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-tm-gray-500 w-[36%]">{translate('field.Description')}</th>

                                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-tm-gray-500 w-[30%]">{translate('field.Account')}</th>

                                <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-tm-gray-500 w-[16%]">{translate('field.Amount')}</th>

                                <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-tm-gray-500 w-[12%]"></th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-tm-gray-200">
                            {data.map((it) => (
                                <tr key={it.index} className="hover:bg-tm-gray-50">
                                    <td className="px-4 py-3 text-sm text-tm-gray-500 whitespace-nowrap">{it.index + 1}</td>

                                    <td className={classNames("px-4 py-3 text-sm text-tm-gray-900", it.isInvalid && "border-l-4 border-red-500")}>
                                        <div className="flex items-center gap-1">
                                            <span>{it.Description}</span>

                                            {(it.descriptionInvalid || it.unmappedInvalid) && (
                                                <Tooltip content={translate('text.missing_required_fields')}>
                                                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0"/>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-2 py-1 text-sm text-tm-gray-900">
                                        <FieldCellSelectSearch
                                            name="AccountID"
                                            value={{value: it.AccountID, label: it.Account}}
                                            onChange={(name, value) => handleAccountInlineChange(it.index, value)}
                                            placeholder={translate('text.no_account_selected')}
                                            isClearable={true}
                                            select={metadata.AccountID}
                                            errorMessage={items[it.index]?.AccountID?.errorMessage}
                                            translate={translate}
                                            openMenuOnFocus={true}
                                        />
                                    </td>

                                    <td className="px-4 py-3 text-sm text-tm-gray-900 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {it.amountInvalid && (
                                                <Tooltip content={translate('text.missing_required_fields')}>
                                                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0"/>
                                                </Tooltip>
                                            )}

                                            {Number(it.Qty) > 1 ? (
                                                <div className="flex flex-col items-end">
                                                    <span>{genericMoneyFormatter(it.Amount)}</span>

                                                    <span className="text-xs text-tm-gray-500">x{it.Qty}</span>

                                                    <span className="font-bold">{genericMoneyFormatter(it.TotalAmount)}</span>
                                                </div>
                                            ) : (
                                                <span>{genericMoneyFormatter(it.Amount)}</span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-4 py-3 text-sm text-tm-gray-900 text-right">
                                        <div className="flex justify-end gap-1">
                                            <ButtonIcon appearance="border" tooltip={translate('btn.edit')} onClick={() => handleToggleEditModal(it)}>
                                                <PencilIcon className="w-4 h-4"/>
                                            </ButtonIcon>

                                            {data.length > 1 && (
                                                <ButtonIcon appearance="border" tooltip={translate('btn.delete')} onClick={() => handleDeleteItem(it)}>
                                                    <TrashIcon className="w-4 h-4"/>
                                                </ButtonIcon>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>

                            <tfoot>
                            <tr className="border-t-2 border-tm-gray-300 font-medium">
                                <td className="px-4 py-3 text-sm text-tm-gray-900"></td>

                                <td className="px-4 py-3 text-sm text-tm-gray-900 font-bold">{translate('text.total')}</td>

                                <td className="px-4 py-3 text-sm text-tm-gray-900"></td>

                                <td className="px-4 py-3 text-sm text-tm-gray-900 text-right font-bold">{genericMoneyFormatter(totalAmountTotal)}</td>

                                <td className="px-4 py-3 text-sm text-tm-gray-900"></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </React.Fragment>
            )}

            {/* Create new item dialog */}
            <ModalSaveResource
                key={createModalKey}
                title={translate('dialog_heading.create_new_item')}
                widthClass="max-w-md"
                limitHeight={true}
                gridColsClass="grid-cols-2"
                show={createModalOpen}
                onClose={handleToggleCreateModal}
                fields={getFields()}
                onSubmit={(params) => {
                    if (params) {
                        onAddNewItem(params);
                        handleToggleCreateModal();
                    }
                }}
                secondaryButtonLabel={translate('btn.save_and_add_more')}
                onSecondaryButtonClick={(params) => {
                    if (params) {
                        onAddNewItem(params);
                        setCreateModalKey((prev) => prev + 1);
                    }
                }}
                disableSecondaryIfNotDirty={true}
                translate={translate}
                metadata={metadata}
                handleInputChange={handleInputChange}
                getRawFields
                htmlAfter={<div className="h-[200px]" aria-hidden="true"/>}
            />

            {/* Edit item dialog */}
            <ModalSaveResource
                key={editModalKey}
                title={translate('dialog_heading.edit_item')}
                widthClass="max-w-md"
                gridColsClass="grid-cols-2"
                limitHeight={true}
                show={editModalOpen}
                onClose={handleToggleEditModal}
                fields={getFields(selectedItem)}
                onSubmit={(params) => {
                    if (params) {
                        if (selectedItem?.index > -1) {
                            onEditItem(params, selectedItem.index);
                        } else {
                            onAddNewItem(params);
                        }
                        handleToggleEditModal();
                    }
                }}
                secondaryButtonLabel={translate('btn.save_and_add_more')}
                onSecondaryButtonClick={(params) => {
                    if (params) {
                        if (selectedItem?.index > -1) {
                            onEditItem(params, selectedItem.index);
                        } else {
                            onAddNewItem(params);
                        }
                        setEditModalKey((prev) => prev + 1);
                        setSelectedItem(undefined);
                    }
                }}
                disableSecondaryIfNotDirty={true}
                translate={translate}
                metadata={metadata}
                handleInputChange={handleInputChange}
                getRawFields
                htmlAfter={<div className="h-[200px]" aria-hidden="true"/>}
            />
        </div>
    );
}
